import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { observer } from "mobx-react"
import {
  Button,
  Card,
  Dropdown,
  Form,
  Modal,
  Select,
  theme,
  Typography,
} from "antd"
import { Header } from "antd/es/layout/layout"
import { UserOutlined } from "@ant-design/icons"

import icon from "@src/app/assets/currency/usdt_trx.svg"
import googleAuthenticator from "@src/app/assets/icons/google-authenticator.png"
import { authStore } from "@src/app/authStore"
import { balancesStore } from "@src/app/balanceStore"
import { InputPattern } from "@src/app/components/InputPattern"
import { Logo } from "@src/app/components/Logo"
import { ModalOtp } from "@src/app/components/ModalOtp"
import { Navigation } from "@src/app/components/navigation"
import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { paths } from "@src/app/paths"
import { NumberPattern } from "@src/app/validators"
import { whitelistStore } from "@src/app/whitelistStore"
import { SettingsOTP } from "@src/pages/settingsOTP"
import { mergeArrays } from "@src/shared/mergeArrays"
import { ReportsPage } from "../reports"
import { SettingsWhitelistPage } from "../settingsWhitelist"
import css from "./index.module.css"

const { Item } = Form

const { Paragraph, Text, Title } = Typography

type Props = {}
interface Options {
  label: string
  value: string
}
export const AffiliateProgramRequest = observer((props: Props) => {
  const { token } = theme.useToken()
  const navigate = useNavigate()
  const { logout, merchant } = authStore
  const { addFormData, balances, createWithdrawal, currencies } = balancesStore
  const { isLoading, whitelist } = whitelistStore
  const globalCurrency = GlobalCurrenciesCollection()
  const [form] = Form.useForm()
  const [isOtpOpen, setIsOtpOpen] = useState(false)
  const [otpValue, setOtpValue] = useState<Array<string>>([])
  const [mergedBalances, setMergedBalances] = useState(balances)
  const [balance, setBalance] = useState("")
  const [withdrawalAvailable, setWithdrawalAvailable] = useState("")
  const [options, setOptions] = useState<Options[]>([])
  const [regex, setRegex] = useState(/^(?!0{2,})\d+(\.\d{1,2})?$/)
  const [disabledSubmit, setDisabledSubmit] = useState(true)
  const [disabledOtpSubmit, setDisabledOtpSubmit] = useState(true)
  const { merchantInfo } = authStore
  const [open, setOpen] = useState(false)

  const handleClose = () => setOpen(false)

  useEffect(() => {
    if (Boolean(balances.length) && Boolean(currencies.length)) {
      setMergedBalances(mergeArrays(balances, currencies))
    }
  }, [balances, currencies])

  useEffect(() => {
    const updateBalance = () => {
      const balance = balances.find(
        (balance) => balance.symbol === "USDT" && balance.network === "tron",
      )

      if (balance) {
        setBalance(balance.balances.available)

        setWithdrawalAvailable(balance.payouts_available.withdrawal)
      }
    }

    const updateOptions = () => {
      const options = whitelist.filter(
        (item) => item.network === "tron" && item.status === "confirmed",
      )

      if (options.length) {
        setOptions(
          options.map(({ address, id }) => ({ label: address, value: id })),
        )
      } else {
        setOptions([])
      }
    }

    const getDecimal = () => {
      const currency = mergedBalances.find(
        (balance) => balance.symbol === "USDT" && balance.network === "tron",
      )

      if (currency) {
        const { decimals } = globalCurrency.getBySymbolNetwork({
          network: currency.network,
          symbol: currency.symbol,
        })!

        const regex = new RegExp(`^(?!0{2,})\\d+(\\.\\d{1,${decimals}})?$`)

        setRegex(regex)
      }
    }

    updateBalance()

    updateOptions()

    form.resetFields()

    getDecimal()
  }, [whitelist, mergedBalances])

  const onsubmitForm = async (value: any) => {
    const { amount, whitelisted_address_id } = value

    addFormData({
      amount,
      symbol: "USDT",
      whitelisted_address_id,
    })

    setIsOtpOpen(true)
  }

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length)

    const { amount, whitelisted_address_id } = form.getFieldsValue()

    const isValid =
      Boolean(whitelisted_address_id) &&
      Boolean(amount) &&
      amount !== "0" &&
      balance !== "0"

    setDisabledSubmit(hasErrors || !isValid)
  }

  const setMaxAmount = () => {
    form.setFieldsValue({ amount: withdrawalAvailable })

    form.validateFields(["amount"])
  }

  const onOtpCancel = () => {
    setIsOtpOpen(false)
  }

  const onOtpChange = (otp: string[]) => {
    setOtpValue(otp)

    setDisabledOtpSubmit(otp.length !== 6)
  }

  const onOtpSubmit = (value: { otp: string[] }) => {
    const otp = value.otp.join("")

    addFormData({ otp })

    createWithdrawal().finally(() => setIsOtpOpen(false))

    setDisabledSubmit(true)

    form.resetFields()
  }

  return (
    <>
      <Header
        style={{
          alignItems: "center",
          background: token.colorBgLayout,
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "32px",
          textAlign: "right",
        }}
      >
        <Logo size="full" />
        <div>
          <Dropdown
            menu={{
              items: [
                {
                  key: "exit",
                  label: (
                    <a
                      onClick={() => logout().then(() => navigate(paths.auth))}
                    >
                      Exit
                    </a>
                  ),
                },
              ],
            }}
          >
            <Button
              icon={<UserOutlined />}
              id="GENERATED_ID_AFFILIATEPROGRAMREQUEST_BUTTON_VOID_1"
              size="large"
            >
              {merchant?.email}
            </Button>
          </Dropdown>
        </div>
      </Header>
      <div className={css.container}>
        <Navigation title="Request my reward" url={paths.affiliateProgram} />
        <div className={css.wrapper}>
          <div className={css.leftSide}>
            <Title className={css.title} level={5}>
              Current Balance
            </Title>
            <Title className={css.balance} level={2}>
              <img alt="USDT" src={icon} /> {`${balance} USDT`}
            </Title>
            <Form
              className={css.form}
              form={form}
              id="GENERATED_ID_AFFILIATEPROGRAMREQUEST_FORM_VOID_1"
              layout="vertical"
              name="withdraw"
              onFieldsChange={handleFormChange}
              onFinish={onsubmitForm}
              size="large"
            >
              <Item
                label="Address"
                name="whitelisted_address_id"
                rules={[
                  { message: "Please select at least one", required: true },
                ]}
              >
                <Select loading={isLoading} options={options} />
              </Item>
              <Paragraph style={{ textAlign: "center" }}>
                Proceed with the transfer of funds, it is necessary to choose
                the whitelisted address. You can add an address for withdrawal
                in the Settings section under Whitelist
              </Paragraph>
              <Item
                label="Amount"
                name="amount"
                rules={[
                  {
                    required: true,
                    validator: (_, value) => {
                      if (Number(value) > Number(withdrawalAvailable)) {
                        return Promise.reject(
                          new Error(
                            `Maximum withdrawal amount: ${withdrawalAvailable}`,
                          ),
                        )
                      }

                      if (!value.match(regex)) {
                        return Promise.reject(
                          new Error("Invalid number of decimal places"),
                        )
                      }

                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <InputPattern
                  addonAfter={
                    <Button
                      id="GENERATED_ID_AFFILIATEPROGRAMREQUEST_BUTTON_MAX_1"
                      onClick={setMaxAmount}
                      size="middle"
                      type="link"
                    >
                      MAX
                    </Button>
                  }
                  pattern={NumberPattern}
                  placeholder="0"
                />
              </Item>
              {balance === "0" && (
                <div className={css.message}>Not sufficient balance</div>
              )}
              {/* TODO: uncomment when fee will be ready */}
              {/* <div className={css.feeWrapper}>
    <Text>{`Withdrawal fees 0.00 ${chosenSymbol || symbol}`}</Text>
    <Text strong>{`To be received 0.00 ${chosenSymbol || symbol}`}</Text>
  </div> */}
              <Item className={css.buttonWrapper}>
                <Button
                  className={css.button}
                  disabled={disabledSubmit}
                  htmlType="submit"
                  id="GENERATED_ID_AFFILIATEPROGRAMREQUEST_BUTTON_CONFIRM_1"
                  loading={isLoading}
                  size="large"
                  type="primary"
                >
                  Confirm
                </Button>
              </Item>
            </Form>
            <ModalOtp
              isDisabled={disabledOtpSubmit}
              isOpen={isOtpOpen}
              onCancel={onOtpCancel}
              onChange={onOtpChange}
              onSubmit={onOtpSubmit}
              value={otpValue}
            />
            <Card
              bodyStyle={{
                display: "flex",
                flexDirection: "column",
                padding: 0,
              }}
              headStyle={{
                border: "none",
                fontSize: "16px",
                marginBottom: "16px",
                minHeight: "19px",
                padding: 0,
              }}
              style={{ border: "none" }}
              title="2FA"
            >
              <Text className={css.text}>
                Secure your account from intruders and activate two-factor
                protection
              </Text>
              <div
                style={{
                  alignItems: "start",
                  border: "1px solid #F2F4F6",
                  borderRadius: "12px",
                  display: "flex",
                  gap: "22px",
                  justifyContent: "space-between",
                  padding: "12px",
                }}
              >
                <img
                  alt="google authenticator"
                  className={css.googleAuthImage}
                  src={googleAuthenticator}
                />
                <div className={css.googleAuth}>
                  <p className={css.title2fa}>Google Authenticator</p>
                  <div className={css.indicator}>
                    <div
                      className={
                        merchantInfo?.is_otp_enabled
                          ? css.indicatorIcon_enable
                          : css.indicatorIcon_disable
                      }
                    ></div>
                    <p
                      className={
                        merchantInfo?.is_otp_enabled
                          ? css.indicatorText_enable
                          : css.indicatorText_disable
                      }
                    >
                      {merchantInfo?.is_otp_enabled ? "Enable" : "Disable"}
                    </p>
                  </div>
                  <p className={css.textAccess}>
                    Get your security code in the app even without internet
                    access
                  </p>
                </div>
                <div className={css.btnBlock}>
                  <Button
                    className={
                      merchantInfo?.is_otp_enabled
                        ? css.btn2fa_enable
                        : css.btn2fa_disable
                    }
                    id="GENERATED_ID_AFFILIATEPROGRAMREQUEST_BUTTON_VOID_2"
                    onClick={() => setOpen(true)}
                  >
                    {merchantInfo?.is_otp_enabled
                      ? "Disable 2FA"
                      : "Enable 2FA"}
                  </Button>
                  <Modal
                    centered
                    closable={false}
                    footer={null}
                    maskClosable={true}
                    maskStyle={{
                      backgroundColor: "#1C6C2A",
                      opacity: 0.15,
                    }}
                    maskTransitionName=""
                    onCancel={() => setOpen(false)}
                    open={open}
                    width={640}
                  >
                    <SettingsOTP onClose={handleClose} />
                  </Modal>
                </div>
              </div>
            </Card>
          </div>
          <div className={css.rightSide}>
            <Title className={css.title} level={5}>
              Withdrawal whitelist
            </Title>
            <SettingsWhitelistPage />
          </div>
        </div>
        {/*<ReportsPage isAffiliate />*/}
      </div>
    </>
  )
})
